import React from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import FinButton from './finance/fin-button';
import FinCheck from './finance/fin-check';

// What Stripe holds about this business, read through our own API and drawn
// in our own markup — so that looking at it costs nothing. Mounting Stripe's
// account_management component puts a sign-in wall in front of the whole
// page whenever the connected account's Stripe authentication has expired,
// and most visits here are a glance, not a change. The component still owns
// every edit; each card's Manage button hands over to it.
//
// Shaped like that component on purpose, section names included, and drawn
// as the mock's record cards (spec 2026-09-21 §5.4): a group label, a card
// whose head is the primary value with the Manage button beside it, then
// label/value rows on a 200px grid.
//
// THE RULE, measured rather than assumed: a field Stripe did not return is
// not drawn. Across ten submitted test accounts (2026-09-16) the business
// half came back on 7-8 of them while company.address landed on 2 and
// product_description on none. A card whose every row is missing goes too,
// and its button with it.
const withScheme = (url) => {
  if (/^https?:\/\//i.test(url)) return url;
  if (/^\/\//.test(url)) return `https:${url}`;
  return `https://${url}`;
};

const Row = ({ label, value, last }) => (
  <div className={`grid grid-cols-1 gap-1 py-[11px] text-[13.5px] sm:grid-cols-[200px_1fr] sm:gap-4 ${last ? '' : 'border-b border-fin-hair'}`}>
    <dt className="flex items-center gap-1.5 text-fin-muted">{label}</dt>
    <dd className="font-medium text-fin-ink">{value}</dd>
  </div>
);

// Rows are data, not children: a <Row> that rendered null would still be a
// truthy React element, so a card decides from the values, before rendering.
const Card = ({ id, heading, title, rows, action, onManage }) => {
  const drawn = rows.filter(([, value]) => value != null && value !== false && value !== '');
  if (drawn.length === 0 && !title) return null;

  return (
    <section id={id} className="scroll-mt-6 space-y-2.5">
      <h2 className="text-[13px] font-semibold text-fin-ink-2">{heading}</h2>
      <div className="rounded-xl border border-fin-border bg-fin-surface px-6">
        <div className="flex items-center justify-between gap-3 border-b border-fin-border py-4">
          <p className="text-[15px] font-bold tracking-[-0.01em] text-fin-ink">{title}</p>
          {/* "Manage", never "Edit": Stripe's component cannot open at a
              section, so pressing this lands at the top of it with Stripe's
              own Edit a press away. These are the only way in. */}
          <FinButton onClick={onManage}>{action}</FinButton>
        </div>
        <dl>
          {drawn.map(([label, value], i) => (
            <Row key={typeof label === 'string' ? label : i} label={label} value={value} last={i === drawn.length - 1} />
          ))}
        </dl>
      </div>
    </section>
  );
};

const statementLabel = (
  <>
    Card statement name
    <button
      type="button"
      aria-label="About the card statement name"
      title="This is the business name customers may see on their card statement."
      className="cursor-help text-fin-faintest"
    >
      <InformationCircleIcon className="h-3.5 w-3.5 stroke-[2.2]" aria-hidden="true" />
    </button>
  </>
);

const verification = (verified) => {
  if (verified === true) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-fin-green">
        <FinCheck />
        Verified
      </span>
    );
  }
  if (verified === false) return <span className="text-[13.5px] font-semibold text-fin-amber">Not yet verified</span>;
  return null;
};

// State, never the number. Stripe holds the EIN; what a restaurant needs to
// know is whether it is on file (mock 4a DEV NOTE: never show EIN, SSN/last
// 4, DOB or ID documents). Three answers, like the representative's
// verification: true and false are Stripe's word and both earn the row —
// "Not provided" is the state a restaurant has to act on, and hiding it
// read as the page having forgotten the row (fe-test, 2026-09-22) — while
// null is Stripe saying nothing, and no row is drawn.
const taxId = (provided) => {
  if (provided === true) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-fin-green">
        <FinCheck />
        Provided
      </span>
    );
  }
  if (provided === false) return <span className="text-[13.5px] font-semibold text-fin-amber">Not provided</span>;
  return null;
};

const ConnectAccountRecord = ({ details, onManage, onManageBank }) => {
  // Falls back to `onManage` so any caller that only ever passes that one
  // handler (this record's sole mount today is Business Details, which
  // passes both) still has somewhere for the bank card's button to go —
  // insurance against a future caller that doesn't wire the bank flow
  // separately, not a description of one that exists.
  const onBank = onManageBank || onManage;

  if (!details?.hasRecord) {
    // Not an error — Stripe answered, it just keeps this account's details
    // to itself. Say that, and leave a way in.
    return (
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-[13.5px] text-fin-muted">
          Stripe hasn&apos;t shared these details with us. You can still see and change them on Stripe.
        </p>
        <FinButton onClick={onManage}>Manage with Stripe</FinButton>
      </div>
    );
  }

  const destination = details.destinationName
    ? `${details.destinationName}${details.destinationLast4 ? ` •••• ${details.destinationLast4}` : ''}`
    : null;

  return (
    <div className="space-y-5">
      <Card
        heading="Business type"
        title={details.businessType}
        rows={[['Business structure', details.businessStructure], ['Location', details.country]]}
        action="Manage business type"
        onManage={onManage}
      />
      <Card
        heading="Business details"
        title={details.legalName}
        rows={[
          ['Legal business name', details.legalName],
          ['Public business name', details.publicName],
          [
            'Business website',
            details.website ? (
              <a href={withScheme(details.website)} target="_blank" rel="noopener noreferrer" className="text-fin-blue hover:text-fin-blue-hover">
                {details.website}
              </a>
            ) : null,
          ],
          // The legal address is the one worth showing and the one Stripe
          // usually withholds; the support address is the same building in
          // practice and comes back far more often.
          ['Business address', details.legalAddress || details.supportAddress],
          ['Industry', details.industry],
          ['Tax ID', taxId(details.taxIdProvided)],
        ]}
        action="Manage business details"
        onManage={onManage}
      />
      <Card
        heading="Public details"
        title="Customer-facing information"
        rows={[
          // Repeated on purpose: the mock draws it under both headings.
          ['Public business name', details.publicName],
          ['Support phone', details.supportPhone],
          ['Support address', details.supportAddress],
          // Not "Statement descriptor": that is Stripe's name for it; what
          // it means to a restaurant is the line their customer reads.
          [statementLabel, details.statementDescriptor],
        ]}
        action="Manage public details"
        onManage={onManage}
      />
      <Card
        heading="Management and ownership"
        title={details.representativeName}
        rows={[
          ['Role', details.representativeRole],
          ['Email', details.representativeEmail],
          ['Verification', verification(details.representativeVerified)],
        ]}
        action="Manage ownership"
        onManage={onManage}
      />
      {/* Where the money lands. Stripe's payouts component owns changing it,
          on Payouts — but that component only names the account once payouts
          exist, so a restaurant that has not been paid yet could not see this
          anywhere. No routing number: the last four identify the account. */}
      <Card
        id="payout-details"
        heading="Payout details"
        title={destination}
        rows={[['Currency', details.destinationCurrency?.toUpperCase() || null]]}
        action="Manage bank account"
        onManage={onBank}
      />
    </div>
  );
};

export default ConnectAccountRecord;
